/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import { END } from 'redux-saga';
import {
    fork, call, put, select, take, takeEvery,
} from 'redux-saga/effects';
import mbpLogger from 'mbp-logger';
import {
    getBillingAddress, getCartOrder, getIfGiftCardApplied, getOrderTotalBalance,
} from '../../Cart/Cart-Selectors';
import { getOrderId, getPaymentInfo } from '../../../../Common/Common-Selectors';
import {
    applyGiftCard,
    applyGiftCardProccessCompleted,
    recalculateGiftCardTotal, recalculateGiftCardTotalCompleted,
    removeGiftCard, removeGiftCardProccessCompleted,
} from './GiftCard-Actions';
import cartServiceClient from '../../../../../../apis/cart-service-apis/cartServiceClient';
import { checkJWT } from '../../../../Member/ducks/Auth/Auth-Operations';
import { calculateCart, calculateCartComplete } from '../CalculateCart/CalculateCart-Actions';
import { callHandleCartFailure } from '../../Cart/Cart-Actions';
import { retrieveCart } from '../../Cart/ducks/RetrieveCart/RetrieveCart-Operations';
import { flagOffShowLoadingMessage, updateShowLoadingMessage } from '../../../../Common/ducks/ShowLoadingMessage/ShowLoadingMessage-Actions';
import { processFeatureErrors } from '../../../../Checkout/ducks/Common/ducks/Error/Error-Operations';
import { clearOrderItemErrors } from '../../../../Checkout/ducks/Common/ducks/Error/Error-Actions';
import { setPaymentMethod } from '../../../../Checkout/ducks/Payment/ducks/PaymentForm/PaymentForm-Actions';

export function* workerRecalculateGiftCardTotal() {
    const cartId = yield select(getOrderId);
    try {
        const isGiftCardApplied = yield select(getIfGiftCardApplied);

        if (isGiftCardApplied) {
            yield put(clearOrderItemErrors(
                cartId,
                'giftCard',
                { errType: 'clearAllFields', field: '' },
            ));

            if (cartId) {
                const jwtToken = yield call(checkJWT);
                yield call(cartServiceClient.calculateCart, { jwtToken, cartId, payload: { calcode: 'reapply-giftcard' } });
            } else {
                mbpLogger.logError({
                    function: 'workerRecalculateGiftCardTotal',
                    appName: process.env.npm_package_name,
                    module: 'mbp-checkout',
                    message: 'Recalculate Gift Card Total - No OrderId',
                    orderId: cartId,
                });
            }
        }
    } catch (ex) {
        yield fork(processFeatureErrors, {
            data: {
                orderItemId: cartId,
                errorFeatureType: 'giftCard',
                errorValues: ex.response?.data,

            },
        });
        mbpLogger.logError({
            function: 'workerRecalculateGiftCardTotal',
            appName: process.env.npm_package_name,
            module: 'mbp-checkout',
            message: 'Recalculate Gift Card Total failed',
            orderId: cartId,
            error: ex,
        });
    }

    yield put(recalculateGiftCardTotalCompleted());
}

function* watcherRecalculateGiftCardTotal() {
    yield takeEvery(recalculateGiftCardTotal().type, workerRecalculateGiftCardTotal);
}

function* applyGC({
    giftcardNumber, giftcardPin, recaptcha, callBackFunc, recaptchaV2, history,
}) {
    try {
        // Show Site Loader
        yield put(updateShowLoadingMessage());

        // Calculate Shipping Befor Apply GC
        yield put(calculateCart());
        yield take(calculateCartComplete().type);

        // Clear Error
        const cartId = yield select(getOrderId);
        const jwtToken = yield call(checkJWT);
        yield put(clearOrderItemErrors(
            cartId,
            'giftCard',
            { errType: 'clearAllFields', field: '' },
        ));
        yield put(clearOrderItemErrors(
            cartId,
            'payment',
            { errType: 'clearAllFields', field: '' },
        ));

        const applyGcResult = yield call(cartServiceClient.giftCard, {
            env: {},
            jwtToken,
            cartId,
            account: giftcardNumber,
            securityCode: giftcardPin,
            recaptcha,
            recaptchaV2,
        });

        if (applyGcResult.data.giftCardOperationStatus === 'SUCCESS') {
            yield call(retrieveCart);
            const cartDetails = yield select(getCartOrder);

            if (cartDetails && cartDetails.cartTotal && Number(cartDetails.cartTotal) === 0) {
                // set default credit card if the gift card cover full amount
                const billingAddress = yield select(getBillingAddress);

                yield put(setPaymentMethod({
                    id: 'CreditCard',
                    name: 'Credit Card',
                }));

                if (
                    history?.location?.pathname && !history.location.pathname.includes('/payment')
                    && (
                        !billingAddress.firstName
                        || !billingAddress.address1
                    )
                ) {
                    history.push(`/checkout/payment/${cartId}`);
                }
            }
        } else {
            yield fork(processFeatureErrors, {
                data: {
                    orderItemId: cartId,
                    errorFeatureType: 'giftCard',
                    errorValues: {
                        errorKey: 'GIFTCARD_ERROR',
                        errorMessage: 'Gift Card Could not be applied',
                    },
                },
            });
        }

        if (callBackFunc) {
            yield call(callBackFunc);
        }
        yield put(applyGiftCardProccessCompleted());

        // Hide Site Loader
        yield put(flagOffShowLoadingMessage());
    } catch (exp) {
        yield put(callHandleCartFailure(exp));

        // Retrieve cart to get the lastest cart data is need if calculate cart was called previously
        yield call(retrieveCart);

        const orderId = yield select(getOrderId);
        yield fork(processFeatureErrors, {
            data: {
                orderItemId: orderId,
                errorFeatureType: 'giftCard',
                errorValues: exp.response?.data,

            },
        });

        mbpLogger.logError({
            appName: process.env.npm_package_name,
            module: 'mbp-checkout',
            function: 'applyGC',
            jsError: exp,
            message: 'Failed to apply Gift Card',
            orderId,
        });
        if (callBackFunc) {
            yield call(callBackFunc);
        }
        yield put(applyGiftCardProccessCompleted());
        // Hide Site Loader
        yield put(flagOffShowLoadingMessage());
    }
}

function* applyGiftCardWatcher() {
    let action = yield take(applyGiftCard().type);
    while (action !== END) {
        yield call(applyGC, action.payload);
        action = yield take(applyGiftCard().type);
    }
}

function* workerRemoveGiftCard(action) {
    const cartId = yield select(getOrderId);
    const giftCardId = action?.payload?.giftCardId || '';
    const history = action?.payload?.history || {};
    const pageType = action?.payload?.pageType || {};
    try {
        // Show Site Loader
        yield put(updateShowLoadingMessage());

        if (giftCardId) {
            const orderTotalBalance = yield select(getOrderTotalBalance);

            // Show Site Loader
            yield put(updateShowLoadingMessage());

            // Clear Error
            yield put(clearOrderItemErrors(
                cartId,
                'giftCard',
                {
                    errType: 'clearAllFields',
                    field: '',
                },
            ));

            // Clear Error
            yield put(clearOrderItemErrors(
                cartId,
                'payment',
                {
                    errType: 'clearAllFields',
                    field: '',
                },
            ));

            const jwtToken = yield call(checkJWT);

            const payload = {
                env: {},
                jwtToken,
                cartId,
                giftCardId,
            };

            const removeGcResult = yield call(cartServiceClient.removeGiftCard, payload);
            if (removeGcResult.data.result === 'SUCCESS') {
                yield put(recalculateGiftCardTotal());
                yield take(recalculateGiftCardTotalCompleted().type);

                yield call(retrieveCart);

                // If the previous balance was 0 and now user have to amount to pay
                // we send the user back to payment page
                const orderId = yield select(getOrderId);
                const paymentInfo = yield select(getPaymentInfo);
                const currentOrderTotalBalance = yield select(getOrderTotalBalance);

                if (
                    pageType === 'review-order'
                    && orderTotalBalance >= 0
                    && Number(currentOrderTotalBalance) > 0
                    && paymentInfo.accountNumber.length < 15
                    && paymentInfo.cvv.length < 3
                    && paymentInfo.nameOnCreditCard.length < 1
                    && (!paymentInfo.expirationMonth
                        || !paymentInfo.expirationYear
                        || !paymentInfo.isFormValid
                    )
                ) {
                    history.push(`/checkout/payment/${orderId}`);
                }
            }

            yield put(removeGiftCardProccessCompleted());

            // Hide Site Loader
            yield put(flagOffShowLoadingMessage());
        }
    } catch (exp) {
        yield put(callHandleCartFailure(exp));

        mbpLogger.logError({
            appName: process.env.npm_package_name,
            module: 'mbp-checkout',
            function: 'workerRemoveGiftCard',
            jsError: exp,
            message: 'Failed to remove Gift Card',
            cartId,
        });

        // Load Error Message
        yield fork(processFeatureErrors, {
            data: {
                orderItemId: cartId,
                errorFeatureType: 'giftCard',
                errorValues: exp.response?.data,
            },
        });
        yield put(removeGiftCardProccessCompleted());

        // Hide Site Loader
        yield put(flagOffShowLoadingMessage());
    }
}

function* watchRemoveGiftCard() {
    yield takeEvery(removeGiftCard().type, workerRemoveGiftCard);
}

export const watchers = [
    fork(watcherRecalculateGiftCardTotal),
    fork(applyGiftCardWatcher),
    fork(watchRemoveGiftCard),
];
